import React, { useCallback, useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import {
  ClipboardList,
  GitMerge,
  Users,
  PenLine,
  Baby,
} from "lucide-react-native";
import {
  CardTitleText,
  MoonDateField,
  MoonFormField,
  MoonSelectField,
} from "@louise/moon-ui";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import {
  MedicalHistoryTextField,
  QuestionLayout,
  CounterInput,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { usePregnancy } from "../PregnancyContext";
import type { PregnancyDetail } from "../PregnancyContext";
import { buildPregnancyNavigationStack } from "../buildPregnancyNavigationStack";
import {
  DELIVERY_TYPE_OPTIONS,
  PREGNANCY_OUTCOME_OPTIONS,
  outcomeHasDeliveryDetails,
} from "../options";
import type { DeliveryType, PregnancyOutcome } from "../options";
import type { PregnancyStackParamList } from "../PregnancyNavigator";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { useDateFormat } from "@src/packages/utils/useDateFormat";

export function PregnancyDetailsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = usePregnancy();
  const navigation = useNavigation<NavigationProp<PregnancyStackParamList>>();
  const { dateFormat } = useDateFormat();

  const pregnancyCount = state.pregnancies.length;

  const outcomeOptions = useMemo(
    () =>
      PREGNANCY_OUTCOME_OPTIONS.map((o) => ({
        label: t(o.labelKey),
        value: o.value,
      })),
    [t],
  );

  const deliveryTypeOptions = useMemo(
    () =>
      DELIVERY_TYPE_OPTIONS.map((o) => ({
        label: t(o.labelKey),
        value: o.value,
      })),
    [t],
  );

  const yesNoOptions = useMemo(
    () => [
      { label: t("med_history.shared.yes"), value: "yes" },
      { label: t("med_history.shared.no"), value: "no" },
    ],
    [t],
  );

  const handleIncrement = useCallback(() => {
    updateState({
      pregnancies: [...state.pregnancies, {}],
    });
  }, [state.pregnancies, updateState]);

  const handleDecrement = useCallback(() => {
    if (state.pregnancies.length <= 1) return;
    const removed = state.pregnancies[state.pregnancies.length - 1];
    const newIdsToDelete = removed?.id
      ? [...state.pregnancyIdsToDelete, removed.id]
      : state.pregnancyIdsToDelete;
    updateState({
      pregnancies: state.pregnancies.slice(0, -1),
      pregnancyIdsToDelete: newIdsToDelete,
      anonymousPregnanciesToDelete:
        state.anonymousPregnanciesToDelete + (removed?.id ? 0 : 1),
    });
  }, [
    state.anonymousPregnanciesToDelete,
    state.pregnancies,
    state.pregnancyIdsToDelete,
    updateState,
  ]);

  const updatePregnancy = (
    index: number,
    updates: Partial<PregnancyDetail>,
  ) => {
    const normalizedUpdates =
      updates.outcome && !outcomeHasDeliveryDetails(updates.outcome)
        ? { ...updates, deliveryDate: null, deliveryType: null }
        : updates;
    const updated = state.pregnancies.map((p, i) =>
      i === index ? { ...p, ...normalizedUpdates } : p,
    );
    updateState({ pregnancies: updated });
  };

  const handleSave = async () => {
    await save();
    const stack = buildPregnancyNavigationStack(state);
    const currentIdx = stack.indexOf("PregnancyDetails");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.gynae.pregnancies.details_title")}
      subtitle={t("med_history.gynae.pregnancies.details_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={false}
      saveLabel={t("med_history.gynae.pregnancies.next")}
    >
      <View style={styles.container}>
        <CounterInput
          label={t("med_history.gynae.pregnancies.pregnancy_counter_label")}
          description={t(
            "med_history.gynae.pregnancies.pregnancy_counter_description",
          )}
          icon={Baby}
          value={pregnancyCount}
          onIncrement={handleIncrement}
          onDecrement={handleDecrement}
          min={1}
          max={99}
        />

        {state.pregnancies.map((pregnancy, index) => (
          <View
            key={pregnancy.id ?? `pregnancy-${index}`}
            style={[GLASS_CARD_STYLE, styles.pregnancyCard]}
          >
            <CardTitleText>
              {t("med_history.gynae.pregnancies.pregnancy_card_title", {
                params: { number: String(index + 1) },
              })}
            </CardTitleText>

            <Text variant="body" size="sm" className="mb-3">
              {t("med_history.gynae.pregnancies.more_details_later")}
            </Text>

            <MoonDateField
              label={t("med_history.gynae.pregnancies.start_date_label")}
              placeholder={dateFormat}
              value={pregnancy.startDate ?? ""}
              onChange={(iso) =>
                updatePregnancy(index, { startDate: iso || null })
              }
            />

            <MoonSelectField
              label={t("med_history.gynae.pregnancies.outcome_label")}
              placeholder={t(
                "med_history.gynae.pregnancies.outcome_placeholder",
              )}
              value={pregnancy.outcome ?? null}
              onChange={(val) =>
                updatePregnancy(index, {
                  outcome: (val as PregnancyOutcome) ?? null,
                })
              }
              options={outcomeOptions}
              labelIcon={ClipboardList}
              style={{ width: "100%" }}
            />

            {outcomeHasDeliveryDetails(pregnancy.outcome) ? (
              <>
                <MoonSelectField
                  label={t("med_history.gynae.pregnancies.delivery_type_label")}
                  placeholder={t(
                    "med_history.gynae.pregnancies.delivery_type_placeholder",
                  )}
                  value={pregnancy.deliveryType ?? null}
                  onChange={(val) =>
                    updatePregnancy(index, {
                      deliveryType: (val as DeliveryType) ?? null,
                    })
                  }
                  options={deliveryTypeOptions}
                  labelIcon={GitMerge}
                  style={{ width: "100%" }}
                />

                <MoonDateField
                  label={t("med_history.gynae.pregnancies.delivery_date_label")}
                  placeholder={dateFormat}
                  value={pregnancy.deliveryDate ?? ""}
                  onChange={(iso) =>
                    updatePregnancy(index, { deliveryDate: iso || null })
                  }
                />
              </>
            ) : null}

            <MoonSelectField
              label={t(
                "med_history.gynae.pregnancies.with_current_partner_label",
              )}
              placeholder={t(
                "med_history.gynae.pregnancies.with_current_partner_placeholder",
              )}
              value={
                pregnancy.withCurrentPartner === true
                  ? "yes"
                  : pregnancy.withCurrentPartner === false
                    ? "no"
                    : null
              }
              onChange={(val) =>
                updatePregnancy(index, {
                  withCurrentPartner:
                    val === "yes" ? true : val === "no" ? false : null,
                })
              }
              options={yesNoOptions}
              labelIcon={Users}
              style={{ width: "100%" }}
            />

            <MoonFormField
              label={t("med_history.gynae.pregnancies.notes_label")}
              labelIcon={PenLine}
            >
              <MedicalHistoryTextField
                placeholder={t(
                  "med_history.gynae.pregnancies.notes_placeholder",
                )}
                value={pregnancy.notes ?? ""}
                onChangeText={(text) =>
                  updatePregnancy(index, { notes: text || null })
                }
                multiline
                numberOfLines={2}
                style={{ width: "100%" }}
              />
            </MoonFormField>
          </View>
        ))}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
    gap: 16,
  },
  pregnancyCard: {
    gap: 16,
    padding: 16,
  },
});
